import React, { useState, useRef, useCallback, useEffect } from 'react';
import { X, Plus, Minus, ArrowsOutCardinal } from 'phosphor-react';
import { Button } from '@/components/ui/button';
import { Slider } from '@/components/ui/slider';

/**
 * ImageCropper - Discord-style avatar editor
 * 
 * Allows users to:
 * - Zoom in/out with a slider
 * - Drag to reposition the image
 * - Preview in a circular frame
 * - Export as square 1:1 image (512x512)
 * 
 * Props:
 * - imageFile: File object of the selected image
 * - onSave: (croppedBlob: Blob) => void - called with cropped image blob
 * - onCancel: () => void - called when user cancels
 * - language: 'en' | 'ptBR' - for translations
 */
export default function ImageCropper({ imageFile, onSave, onCancel, language = 'en' }) {
  const canvasRef = useRef(null);
  const containerRef = useRef(null);
  const [imageSrc, setImageSrc] = useState(null);
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageElement, setImageElement] = useState(null);
  
  // Crop state
  const [zoom, setZoom] = useState(1);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [minZoom, setMinZoom] = useState(1);
  
  // Output size (square)
  const OUTPUT_SIZE = 512;
  // Preview circle size
  const PREVIEW_SIZE = 280;
  
  // Translations
  const t = {
    title: language === 'ptBR' ? 'Ajustar Foto de Perfil' : 'Adjust Profile Picture',
    zoom: language === 'ptBR' ? 'Zoom' : 'Zoom',
    dragHint: language === 'ptBR' ? 'Arraste para reposicionar' : 'Drag to reposition',
    save: language === 'ptBR' ? 'Salvar' : 'Save',
    cancel: language === 'ptBR' ? 'Cancelar' : 'Cancel',
  };

  // Load image when file changes
  useEffect(() => {
    if (!imageFile) return;
    
    const reader = new FileReader();
    reader.onload = (e) => {
      setImageSrc(e.target.result);
    };
    reader.readAsDataURL(imageFile);
  }, [imageFile]);

  // Calculate initial zoom and position when image loads
  useEffect(() => {
    if (!imageSrc) return;
    
    const img = new Image();
    img.onload = () => {
      setImageElement(img);
      
      // Calculate minimum zoom to fill the circle
      // The image must completely cover the preview area
      const scaleX = PREVIEW_SIZE / img.width;
      const scaleY = PREVIEW_SIZE / img.height;
      const initialZoom = Math.max(scaleX, scaleY);
      
      setMinZoom(initialZoom);
      setZoom(initialZoom);
      setPosition({ x: 0, y: 0 });
      setImageLoaded(true);
    };
    img.src = imageSrc;
  }, [imageSrc]);

  // Get scaled dimensions
  const getScaledDimensions = useCallback(() => {
    if (!imageElement) return { width: 0, height: 0 };
    return {
      width: imageElement.width * zoom,
      height: imageElement.height * zoom,
    };
  }, [imageElement, zoom]);

  // Clamp position to keep image within bounds
  const clampPosition = useCallback((pos, scaledWidth, scaledHeight) => {
    // The image center can move, but must keep the preview area covered
    const maxX = (scaledWidth - PREVIEW_SIZE) / 2;
    const maxY = (scaledHeight - PREVIEW_SIZE) / 2;
    
    return {
      x: Math.max(-maxX, Math.min(maxX, pos.x)),
      y: Math.max(-maxY, Math.min(maxY, pos.y)),
    };
  }, []);

  // Handle mouse/touch drag start
  const handleDragStart = useCallback((e) => {
    e.preventDefault();
    setIsDragging(true);
    
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    
    setDragStart({
      x: clientX - position.x,
      y: clientY - position.y,
    });
  }, [position]);

  // Handle mouse/touch move
  const handleDragMove = useCallback((e) => {
    if (!isDragging) return;
    e.preventDefault();
    
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    
    const { width, height } = getScaledDimensions();
    const newPos = {
      x: clientX - dragStart.x,
      y: clientY - dragStart.y,
    };
    
    setPosition(clampPosition(newPos, width, height));
  }, [isDragging, dragStart, getScaledDimensions, clampPosition]);

  // Handle drag end
  const handleDragEnd = useCallback(() => {
    setIsDragging(false);
  }, []);

  // Add global mouse/touch listeners for drag
  useEffect(() => {
    if (isDragging) {
      window.addEventListener('mousemove', handleDragMove);
      window.addEventListener('mouseup', handleDragEnd);
      window.addEventListener('touchmove', handleDragMove, { passive: false });
      window.addEventListener('touchend', handleDragEnd);
    }
    
    return () => {
      window.removeEventListener('mousemove', handleDragMove);
      window.removeEventListener('mouseup', handleDragEnd);
      window.removeEventListener('touchmove', handleDragMove);
      window.removeEventListener('touchend', handleDragEnd);
    };
  }, [isDragging, handleDragMove, handleDragEnd]);

  // Handle zoom change
  const handleZoomChange = useCallback((newZoom) => {
    const clampedZoom = Math.max(minZoom, Math.min(3, newZoom));
    setZoom(clampedZoom);
    
    // Recalculate position bounds after zoom
    if (imageElement) {
      const newWidth = imageElement.width * clampedZoom;
      const newHeight = imageElement.height * clampedZoom;
      setPosition(prev => clampPosition(prev, newWidth, newHeight));
    }
  }, [minZoom, imageElement, clampPosition]);

  // Render preview canvas
  useEffect(() => {
    if (!imageLoaded || !imageElement || !canvasRef.current) return;
    
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    
    // Set canvas size to preview size
    canvas.width = PREVIEW_SIZE;
    canvas.height = PREVIEW_SIZE;
    
    // Clear canvas
    ctx.fillStyle = '#0A0A0A';
    ctx.fillRect(0, 0, PREVIEW_SIZE, PREVIEW_SIZE);
    
    // Calculate draw position (centered with offset)
    const { width, height } = getScaledDimensions();
    const drawX = (PREVIEW_SIZE - width) / 2 + position.x;
    const drawY = (PREVIEW_SIZE - height) / 2 + position.y;
    
    // Draw image
    ctx.drawImage(imageElement, drawX, drawY, width, height);
  }, [imageLoaded, imageElement, zoom, position, getScaledDimensions]);

  // Handle save - export cropped image
  const handleSave = useCallback(() => {
    if (!imageElement) return;
    
    // Create output canvas
    const outputCanvas = document.createElement('canvas');
    outputCanvas.width = OUTPUT_SIZE;
    outputCanvas.height = OUTPUT_SIZE;
    const ctx = outputCanvas.getContext('2d');
    
    // Calculate the crop area from the preview
    // Scale factor from preview to output
    const scaleFactor = OUTPUT_SIZE / PREVIEW_SIZE;
    
    // Calculate where to sample from the source image
    const { width, height } = getScaledDimensions();
    const sourceX = (width - PREVIEW_SIZE) / 2 - position.x;
    const sourceY = (height - PREVIEW_SIZE) / 2 - position.y;
    
    // Convert from scaled coordinates back to original image coordinates
    const srcX = sourceX / zoom;
    const srcY = sourceY / zoom;
    const srcWidth = PREVIEW_SIZE / zoom;
    const srcHeight = PREVIEW_SIZE / zoom;
    
    // Draw the cropped portion at output size
    ctx.drawImage(
      imageElement,
      srcX, srcY, srcWidth, srcHeight,
      0, 0, OUTPUT_SIZE, OUTPUT_SIZE
    );
    
    // Export as blob
    outputCanvas.toBlob((blob) => {
      if (blob) {
        onSave(blob);
      }
    }, 'image/jpeg', 0.92);
  }, [imageElement, zoom, position, getScaledDimensions, onSave]);

  if (!imageFile) return null;

  return (
    <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4">
      <div 
        className="bg-[#121212] border border-white/10 rounded-lg w-full max-w-md overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-white/10">
          <h2 className="text-white font-medium">{t.title}</h2>
          <button 
            onClick={onCancel}
            className="p-1 hover:bg-white/10 rounded transition-colors"
          >
            <X size={20} className="text-white/60" />
          </button>
        </div>
        
        {/* Preview Area */}
        <div className="p-6 flex flex-col items-center">
          {/* Circular Preview Container */}
          <div 
            ref={containerRef}
            className="relative mb-4"
            style={{ width: PREVIEW_SIZE, height: PREVIEW_SIZE }}
          >
            {/* Dark overlay with circular cutout */}
            <div 
              className="absolute inset-0 pointer-events-none z-10"
              style={{
                background: `radial-gradient(circle at center, transparent ${PREVIEW_SIZE/2 - 2}px, rgba(0,0,0,0.7) ${PREVIEW_SIZE/2}px)`,
              }}
            />
            
            {/* Circular border */}
            <div 
              className="absolute inset-0 pointer-events-none z-20 rounded-full border-2 border-white/30"
              style={{ 
                boxShadow: '0 0 0 4px rgba(0,0,0,0.5)',
              }}
            />
            
            {/* Canvas with image */}
            <canvas
              ref={canvasRef}
              onMouseDown={handleDragStart}
              onTouchStart={handleDragStart}
              className="rounded-full cursor-move"
              style={{
                width: PREVIEW_SIZE,
                height: PREVIEW_SIZE,
                touchAction: 'none',
              }}
              data-testid="cropper-canvas"
            />
            
            {/* Loading state */}
            {!imageLoaded && (
              <div className="absolute inset-0 flex items-center justify-center bg-[#0A0A0A] rounded-full">
                <div className="w-8 h-8 border-2 border-white/20 border-t-white rounded-full animate-spin" />
              </div>
            )}
          </div>
          
          {/* Drag hint */}
          <div className="flex items-center gap-2 text-white/40 text-xs mb-6">
            <ArrowsOutCardinal size={14} />
            <span>{t.dragHint}</span>
          </div>
          
          {/* Zoom Slider */}
          <div className="w-full max-w-xs space-y-2">
            <div className="flex items-center justify-between text-white/60 text-xs">
              <span>{t.zoom}</span>
              <span>{Math.round((zoom / minZoom) * 100)}%</span>
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={() => handleZoomChange(zoom - 0.1)}
                disabled={zoom <= minZoom}
                className="p-1 hover:bg-white/10 rounded transition-colors disabled:opacity-30"
              >
                <Minus size={18} className="text-white/60" />
              </button>
              
              <Slider
                value={[zoom]}
                min={minZoom}
                max={3}
                step={0.01}
                onValueChange={([v]) => handleZoomChange(v)}
                className="flex-1"
                data-testid="zoom-slider"
              />
              
              <button
                onClick={() => handleZoomChange(zoom + 0.1)}
                disabled={zoom >= 3}
                className="p-1 hover:bg-white/10 rounded transition-colors disabled:opacity-30"
              >
                <Plus size={18} className="text-white/60" />
              </button>
            </div>
          </div>
        </div>
        
        {/* Footer */}
        <div className="flex gap-3 p-4 border-t border-white/10">
          <Button
            variant="outline"
            onClick={onCancel}
            className="flex-1 border-white/20 text-white/70 hover:text-white hover:bg-white/10"
            data-testid="cropper-cancel"
          >
            {t.cancel}
          </Button>
          <Button
            onClick={handleSave}
            disabled={!imageLoaded}
            className="flex-1 bg-[#00E676] hover:bg-[#00E676]/80 text-black font-medium"
            data-testid="cropper-save"
          >
            {t.save}
          </Button>
        </div>
      </div>
    </div>
  );
}
